import _ from 'lodash'
import { EMPTY, Observable, of, throwError } from 'rxjs'
import { catchError, delayWhen, distinctUntilChanged, first, last, map, pluck, tap } from 'rxjs/operators'

import { getTranslatedString, SearchResponse, DataStoreOptions, EntityField, SearchQuery } from '@skinsoft/types'
import { itemSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'
import { fetch } from '@skinsoft/network'
import { OfflineDataFetcher } from '@skinsoft/offline'
import { DataStore } from "@skinsoft/data-store"
import { ThesaurusTermDataStore, ItemDataStore } from '@skinsoft/entity'

declare const config
const FALLBACK_LOCALE = config.fallback_locale
declare const ENTITY_FIELDS_TYPES_FOR_SYNC_DOWN: string[]

export class EntityFieldDataStore extends DataStore<EntityField> {

    /**
     * Retrieve string value from entity and entityField (useful to sort fields)
     * @param value 
     * @param entityField 
     * @param locale 
     */
    public static async getValueFromEntityField(value: any, entityField: EntityField, locale: string = FALLBACK_LOCALE): Promise<string> {
        const efPath = _.get(entityField, 'path')
        const efIsReference = _.get(entityField, 'type.referenceField')
        const efType = _.get(entityField, 'type.type')

        const rawValue = _.get(value, efPath);

        if (!efIsReference) {
            switch (efType) {
                case "String":
                    return rawValue as string;
                case "TranslatedString":
                    {
                        const returnValue = getTranslatedString(rawValue, locale) as string || "";
                        return returnValue;
                    }
                case "SkinDate":
                    {
                        const returnValue: number = _.get(rawValue, 'startInterval.date') as number;
                        return returnValue ? returnValue.toString() : "0";
                    }
                default:
                    return "";
            }
        } else {
            switch (efType) {
                case "Item":
                    const item = await ItemDataStore.getInstance().fetchOne(rawValue).pipe(first()).toPromise();
                    return itemSelectors.title(item, locale); // Maybe improve ?
                case "ThesaurusTerm":
                    const term = await ThesaurusTermDataStore.getInstance().fetchOne(rawValue).pipe(first()).toPromise();
                    const returnValue = getTranslatedString(thesaurusTermSelectors.preferredTerm(term), locale) || "";
                    return returnValue;
                // TODO other entity types if needed
                default:
                    return ""
            }
        }
    }

    public static getInstance() {
        if (!EntityFieldDataStore.instance) {
            EntityFieldDataStore.instance = new EntityFieldDataStore();
        }

        return EntityFieldDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$entityFieldDataStore = EntityFieldDataStore.getInstance();
    }

    private static instance: EntityFieldDataStore = null as any;

    constructor() {
        // See this.searchAll (explains why we don't use 'entityField' for types)
        const typesForSearch = ['physicalMuseumItem', 'recolement', 'auditProject', 'conditionReport', 'thesaurusTerm', 'thesaurus', 'TranslatedString']
        super('/entityField', typesForSearch)
        this.emulateSearch = true;
    }

    /**
     * We cannot search on entityFields so we override searchAll method to emulate the search behavior.
     * Each query for a different entity type is considered as a search "page"
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param options 
     */
    public searchAll(searchQuery: Omit<SearchQuery, 'types'> & { types: string[] }, pathAndValueToCheck?: any, 
        searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, 
        onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false): Observable<SearchResponse<EntityField>> {
        
        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0
        if(!searchQuery.language) searchQuery.language = FALLBACK_LOCALE

        if (searchQuery.page < searchQuery.types.length) {
            const url = `${this.endpoint}/${searchQuery.types[searchQuery.page]}`
            return this.proxy.fetch(url)
                .pipe(
                    pluck("_embedded", "entityFields"),
                    map(
                        data => {
                            return (
                                {
                                    results: data,
                                    totalSize: searchQuery.types.length
                                } as SearchResponse<EntityField>
                            )
                        }
                    )
                )
        }
        else {
            return of({
                allData: [],
                results: [],
                totalSize: searchQuery.types.length,
            } as SearchResponse<EntityField>)
        }
    }

    public async getEntityFieldId(entityFieldName: string,
        entityName: string,
        schemaName: string): Promise<string> {

        // Online
        if (window.navigator.onLine) {
            const entityField = await this.proxy.fetch(`${this.endpoint}/name`, {
                entityFieldName,
                entityName,
                schemaName
            }).toPromise();
            this.offlineDataFetcher.updateOne(entityField.id, entityField)
            return entityField.id;
        }
        // Offline
        else {
            const matchingEntityField = await this.offlineDataFetcher.fetchOneFromPredicate(
                (entityField: EntityField) => {
                    return entityField.entity === entityName && entityField.schema === schemaName && entityField.name === entityFieldName
                }
            ).toPromise();
            if (matchingEntityField) {
                return matchingEntityField.id;
            } else {
                return "";
            }
        }
    }

    public fetchOne(id: string, networkFirst = false): Observable<EntityField> {
        // if online
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) {
            const uri = `${this.endpoint}/id/${id}`;

            const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
                last(),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            );
            let source$ = network$;

            if (networkFirst) {
                source$ = source$.pipe(delayWhen(() => network$));
            }

            return source$.pipe(
                distinctUntilChanged(_.isEqual),
                this.mergeUpdateCacheDataToOneResult.bind(this),
            );
        }
        // if offline
        else {
            return this.fetchOneOfflineMode(id);
        }
    }
}